import { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import { useLanguage } from "../../contexts/LanguageContext";
import { useUser } from "../../contexts/UserContext";
import Header from "../Header/Header";

import { buildErrorAuth } from "./buildError";
import GoogleLoginButton from "./GoogleLoginButton";

import "./UserAuth.css";

const UserAuth = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { login, register, googleLogin } = useUser();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      navigate("/", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = t("login.emailRequired");
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t("login.emailFormat");
    }

    if (!formData.password) {
      newErrors.password = t("login.passwordRequired");
    } else if (formData.password.length < 6) {
      newErrors.password = t("login.passwordMinLength");
    }

    if (!isLogin) {
      if (!formData.name) {
        newErrors.name = t("login.nameRequired");
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = t("login.passwordMismatch");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
      } else {
        await register({
          username: formData.name,
          email: formData.email,
          password: formData.password,
          repeatPassword: formData.confirmPassword,
        });
      }
      navigate("/", { replace: true });
    } catch (error) {
      const authError = buildErrorAuth(error, t);
      setErrors({ submit: authError });
    } finally {
      setIsSubmitting(false);
    }
  };

  const switchAuthMode = () => {
    setIsLogin(!isLogin);
    setFormData({
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
    });
    setErrors({});
  };

  const handleGoogleSuccess = async (userData) => {
    setIsSubmitting(true);
    setErrors({}); // Clear previous errors

    try {
      await googleLogin(userData.credential, userData);
      navigate("/", { replace: true });
    } catch (error) {
      const authError = buildErrorAuth(error, t);
      setErrors({ submit: authError });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleError = () => {
    setErrors({ submit: t("login.googleAuthFailed") });
  };

  return (
    <div className="user-auth-page">
      <Header />
      <>
        <section
          className="hero-section ptb-100 background-img full-screen"
          style={{
            background:
              "url('assets/img/hero-2.webp')no-repeat center center / cover",
          }}
        >
          <div className="container">
            <div className="row align-items-center justify-content-between pt-5 pt-sm-5 pt-md-5 pt-lg-0">
              <div className="col-md-7 col-lg-6">
                <div className="hero-content-left text-white">
                  <h1 className="text-white">
                    {isLogin ? t("auth.login.title") : t("auth.register.title")}
                  </h1>
                  <p className="lead">
                    {isLogin
                      ? t("auth.login.subtitle")
                      : t("auth.register.subtitle")}
                  </p>
                </div>
              </div>
              <div className="col-md-5 col-lg-5">
                <div className="card login-signup-card shadow-lg mb-0">
                  <div className="card-body px-md-5 py-5">
                    <div className="mb-5">
                      <h5 className="h3">Snappin</h5>
                      <p className="text-muted mb-0">
                        {isLogin
                          ? t("auth.login.subtitle")
                          : t("auth.register.subtitle")}
                      </p>
                    </div>

                    <form onSubmit={handleSubmit} className="login-signup-form">
                      {isLogin && (
                        <GoogleLoginButton
                          onSuccess={handleGoogleSuccess}
                          onError={handleGoogleError}
                          disabled={isSubmitting}
                        />
                      )}

                      <div className="form-group">
                        <label className="pb-1">{t("auth.login.email")}</label>
                        <div className="input-group input-group-merge">
                          <div className="input-icon">
                            <span className="ti-email color-primary"></span>
                          </div>
                          <input
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder={t("auth.login.emailPlaceholder")}
                            className={`form-control ${
                              errors.email ? "error" : ""
                            }`}
                            type="email"
                          />
                        </div>
                        {errors.email && (
                          <span className="error-message">{errors.email}</span>
                        )}
                      </div>

                      {!isLogin && (
                        <div className="form-group">
                          <label className="pb-1">{t("auth.login.name")}</label>
                          <div className="input-group input-group-merge">
                            <div className="input-icon">
                              <span className="ti-user color-primary"></span>
                            </div>
                            <input
                              id="name"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              placeholder={t("auth.login.namePlaceholder")}
                              className={`form-control ${
                                errors.name ? "error" : ""
                              }`}
                              type="text"
                            />
                          </div>
                          {errors.name && (
                            <span className="error-message">{errors.name}</span>
                          )}
                        </div>
                      )}

                      <div className="form-group">
                        <div className="row">
                          <div className="col">
                            <label className="pb-1">
                              {t("auth.login.password")}
                            </label>
                          </div>
                        </div>
                        <div className="input-group input-group-merge">
                          <div className="input-icon">
                            <span
                              onClick={togglePasswordVisibility}
                              className={`${
                                showPassword ? "ti-unlock" : "ti-lock"
                              } color-primary`}
                            ></span>
                          </div>
                          <input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            placeholder={t("auth.login.passwordPlaceholder")}
                            className={`form-control ${
                              errors.email ? "error" : ""
                            }`}
                          />
                        </div>
                      </div>

                      {!isLogin && (
                        <div className="form-group">
                          <div className="row">
                            <div className="col">
                              <label className="pb-1">
                                {t("auth.login.confirmPassword")}
                              </label>
                            </div>
                          </div>
                          <div className="input-group input-group-merge">
                            <div className="input-icon">
                              <span
                                onClick={togglePasswordVisibility}
                                className={`${
                                  showPassword ? "ti-unlock" : "ti-lock"
                                } color-primary`}
                              ></span>
                            </div>
                            <input
                              type={showPassword ? "text" : "password"}
                              id="confirmPassword"
                              name="confirmPassword"
                              value={formData.confirmPassword}
                              onChange={handleInputChange}
                              placeholder={t("auth.login.confirmPassword")}
                              className={`form-control ${
                                errors.email ? "error" : ""
                              }`}
                            />
                          </div>
                          {errors.confirmPassword && (
                            <span className="error-message">
                              {errors.confirmPassword}
                            </span>
                          )}
                        </div>
                      )}

                      <button
                        type="submit"
                        className="btn btn-lg btn-block solid-btn border-radius mt-4 mb-3"
                        disabled={isSubmitting}
                      >
                        {isLogin
                          ? t("auth.login.signIn")
                          : t("auth.login.signUp")}
                      </button>
                    </form>
                  </div>
                  <div className="card-footer bg-transparent border-top px-md-5">
                    <div className="auth-switch">
                      <p>
                        {isLogin
                          ? t("auth.login.noAccount")
                          : t("auth.register.alreadyHaveAccount")}
                        <button
                          type="button"
                          className="btn btn-lg btn-block"
                          onClick={switchAuthMode}
                          style={{ marginLeft: "0.5rem" }}
                        >
                          {isLogin
                            ? t("auth.register.registerButton")
                            : t("auth.login.signIn")}
                        </button>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bottom-img-absolute">
            <img
              src="assets/img/hero-bg-shape-1.svg"
              alt="wave shape"
              className="img-fluid"
            />
          </div>
        </section>
      </>
    </div>
  );
};

export default UserAuth;
